/* eslint-disable complexity */
import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import API from '@scale/frontend-lib/dashboard-api';
import {
  TextCollectionField,
  RankingOrderField,
  TextCollectionFieldType,
  TextField,
  NumberField,
} from '@scale/shared/types/params/textcollection';
import { RLHFParams } from '../../components/scalers/tasks/textcollection/TextCollectionForm';
import {
  TextCollectionAnnotation,
  QueryableTextAndResultsAnnotationEntry,
} from '@scale/shared/types/response/textcollection';
import _ from 'lodash';
import {
  RlhfBenchmarkType,
  ChatHistory,
  GetCompletionsResponse,
  Interaction,
} from '@scale/shared/types/rlhf';
import { invariant } from '@scale/shared/types/utils';
import { sleep } from '@scale/shared/utils';
import { isBlankField } from '@scale/shared/textcollection/helpers';
import { LintDiagnostic, LintSeverity } from '@scale/shared/task_lint/task_linter';
import { useLocalStorage } from 'react-use-v13';
import apiFetch from '@scale/frontend-lib/api-fetch';
import logger from '@scale/frontend-lib/logging';
import { Event, logEvent } from 'react-scale/lib/events';
import uuid from 'uuid';
import { Button } from 'design-system';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { MARKDOWN_DIVIDER } from 'design-system/SanitizedMarkdown';
import {
  BulbaBardEndpointOperation,
  StrategyName,
  getStrategyFromUrl,
} from '@scale/shared/types/chat-completion';
import { relativeLevenshteinDistance } from '@scale/shared/selfServe/helpers';
import { addCodeFieldsToMarkdown } from 'react-scale/components/scalers/tasks/textcollection/helperText';
import { getMessagesFromCompletedInteractions } from '@scale/shared/selfServe/rlhfHelpers';
import { produce } from 'immer';
import { NLPLintCondition } from '@scale/shared/task_lint/linters/categorization';
import SAlert from 'react-s-alert';
import { useAttemptBehaviorLog } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/AttemptBehaviorLogContext';
import { MessageRole } from '@scale/shared/types/chat';

type ChatbotRankingContextType = {
  enabled: boolean;
  readOnly: boolean;
  isReview: boolean;
  history: ChatHistory;
  setEnabled: (_v: boolean) => void;
  setReadOnly: (_v: boolean) => void;
  setIsReview: (_v: boolean) => void;
  nextInteraction?: () => void;
  previousInteraction?: () => void;
  jumpToEnd?: () => void;
  transcript: string | undefined;
  submitPrompts: (
    prompts: string[],
    topic?: string,
    metadata?: Record<string, any>,
  ) => Promise<void>;
  fieldsToShow: TextCollectionField[];
  setTaxonomy: (
    fields: TextCollectionField[],
    perConversationFieldIds: string[],
    perTurnFieldIds?: string[],
  ) => void;
  rlhfParams: RLHFParams | undefined;
  setRLHFParams: (params: RLHFParams) => void;
  submitChatbotForm: () => void;
  rangedMultiTurnEndConversation: () => void;
  formatSections: (_sections: JSX.Element[]) => JSX.Element[];
  isDone: boolean;
  rangedMultiTurnCanSubmit: boolean;
  annotations: Record<string, TextCollectionAnnotation>;
  setAnnotations: (_annotations: Record<string, TextCollectionAnnotation>) => void;
  taskCustomer: string | undefined;
  setTaskCustomer: (taskCustomer: string) => void;
  candidateBestCompletion: string | undefined;
  selectedCompletion: number;
  setSelectedCompletion: (_completion: number) => void;
  useChatbotV2: boolean;
  selectedInteraction: number;
  setSelectedInteraction: (_interaction: number) => void;
  onPerConvoField: boolean;
  gotoSelectedInteraction: (_interaction: number) => void;
  overrideViewFields: boolean;
  erroredItems: string[];
  checkIfItemBlank: (_interaction: number, _completion: number) => boolean;
  isMakingChanges: boolean;
  perConversationFields: any[];
  perResponseFields: any[];
  perTurnFields: any[];
  hideReviewNav: boolean;
  hideNextButton: boolean;
  nextButtonDisabled: string | undefined;
  shouldUpdateBestCompletion: boolean;
  setShouldUpdateBestCompletion: (v: boolean) => void;
  chatBoxTexts: string[];
  setChatBoxTexts: (texts: string[]) => void;
  canResetChat: boolean;
  resetChat: () => void;
  isStreaming: boolean;
  autoSpecText: string;
  setAutoSpecText: React.Dispatch<React.SetStateAction<string>>;
  autoSpecLoading: boolean;
  setAutoSpecLoading: React.Dispatch<React.SetStateAction<boolean>>;
  critiqueText: string;
  setCritiqueText: React.Dispatch<React.SetStateAction<string>>;
  critiqueResponseChanged: number;
  setAttachments: React.Dispatch<React.SetStateAction<any>>;
  isRangedMultiTurn: boolean;
  handleUndo: () => void;
  stopStreaming: (() => void) | undefined;
  startBranchConversation: () => void;
  reviewNoBranching: boolean;
  candidateEditedPrompt: string | undefined;
  interactionNumber: number | undefined;
  hasItemsOnRankingEditPage: boolean | undefined;
  additionalResponseData: Record<string, any>;
  setAdditionalResponseData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  useEditPrompt: boolean | undefined;
  submitChatbotFormAsync: () => void;
  skipPromptValidation: boolean;
  streamingInteraction: Interaction | undefined;
  resetLastInteraction: () => Record<string, TextCollectionAnnotation> | void;
};

const ChatbotRankingContext = React.createContext<ChatbotRankingContextType>({
  enabled: false,
  readOnly: false,
  isReview: false,
  history: { interactions: [] },
  setEnabled: (_v: boolean) => {},
  setReadOnly: (_v: boolean) => {},
  setIsReview: (_v: boolean) => {},
  nextInteraction: undefined,
  previousInteraction: undefined,
  jumpToEnd: undefined,
  transcript: '',
  submitPrompts: () => Promise.resolve(),
  fieldsToShow: [],
  setTaxonomy: (
    _fields: TextCollectionField[],
    _perConversationFieldIds: string[],
    _perTurnFieldIds?: string[],
  ) => {},
  rlhfParams: undefined,
  setRLHFParams: (_params: RLHFParams) => {},
  submitChatbotForm: () => {},
  rangedMultiTurnEndConversation: () => {},
  formatSections: (sections: JSX.Element[]) => sections,
  annotations: {},
  setAnnotations: (_annotations: Record<string, TextCollectionAnnotation>) => {},
  isDone: false,
  rangedMultiTurnCanSubmit: false,
  taskCustomer: undefined,
  setTaskCustomer: (_taskCustomer: string) => {},
  candidateBestCompletion: undefined,
  selectedCompletion: 0,
  setSelectedCompletion: (_completion: number) => {},
  useChatbotV2: false,
  selectedInteraction: 0,
  setSelectedInteraction: (_interaction: number) => {},
  onPerConvoField: false,
  gotoSelectedInteraction: (_interaction: number) => {},
  overrideViewFields: false,
  perConversationFields: [],
  perResponseFields: [],
  perTurnFields: [],
  erroredItems: [],
  checkIfItemBlank: (_interaction: number, _completion: number) => {
    return false;
  },
  isMakingChanges: false,
  hideReviewNav: false,
  hideNextButton: false,
  nextButtonDisabled: undefined,
  shouldUpdateBestCompletion: false,
  setShouldUpdateBestCompletion: () => {},
  chatBoxTexts: ['Type your prompt'],
  setChatBoxTexts: (_text: string[]) => {},
  canResetChat: false,
  resetChat: () => {},
  isStreaming: false,
  autoSpecText: '',
  setAutoSpecText: () => {},
  autoSpecLoading: false,
  setAutoSpecLoading: () => {},
  critiqueText: '',
  setCritiqueText: () => {},
  critiqueResponseChanged: 0,
  setAttachments: () => {},
  isRangedMultiTurn: false,
  handleUndo: () => {},
  stopStreaming: undefined,
  startBranchConversation: () => {},
  reviewNoBranching: false,
  candidateEditedPrompt: undefined,
  interactionNumber: undefined,
  hasItemsOnRankingEditPage: true,
  additionalResponseData: {},
  setAdditionalResponseData: () => {},
  useEditPrompt: false,
  submitChatbotFormAsync: () => {},
  skipPromptValidation: false,
  streamingInteraction: undefined,
  resetLastInteraction: () => {},
});

const rankingField: RankingOrderField = {
  field_id: 'response_ranking',
  type: TextCollectionFieldType.RankingOrder,
  first_label: 'Best',
  last_label: 'Worst',
  // flexible_positions?: boolean;
  num_items_to_rank: 2,
  // flexible_options?: boolean;
  num_options: 2,
  title: 'Rank the options from best to worst.',
  required: true,
};

const fairRankingField: RankingOrderField = {
  field_id: 'response_ranking',
  type: TextCollectionFieldType.RankingOrder,
  first_label: 'Most preferred',
  last_label: 'Least preferred',
  num_items_to_rank: 2,
  num_options: 2,
  title: 'Which response (option) do you prefer?',
  hint: 'Read the 2 responses(options) generated by AI, and ask yourself which response (option) do you prefer?',
  required: true,
};

const fairOptionQualityFieldMultiTurn: NumberField = {
  required: true,
  type: TextCollectionFieldType.Number,
  use_slider: true,
  field_id: 'option_quality',
  title: 'Option quality',
  min: 1,
  max: 7,
  step: 1,
};

const pipelineV2RankingHint =
  'Read the 3 responses(options) generated by AI, and ask yourself which response (option) do you prefer?';

// crab buffalo hack
const writeYourOwnCompletionField: TextField = {
  type: TextCollectionFieldType.Text,
  field_id: 'edit_completion',
  title: 'Write your own response',
  hint: `The model wasn't able to generate a response because the input is too large, please write your own response to the prompt.`,
  use_grammar_check: true,
};

interface ChatbotRankingProviderProps {
  useChatbotV2?: boolean;
  lintErrors?: LintDiagnostic[];
  assignmentId?: string;
  useFairStreaming?: boolean;
  isStudio?: boolean;
  projectId?: string;
  useEditPrompt?: boolean;
  allowPastingInRLHFEditResponse?: boolean;
  forceEditCompletionLevenshteinThreshold?: number;
  metadata?: any;
  perTurnCheck?: () => any;
  useAutoCritique?: boolean;
  children?: React.ReactNode;
}

export const ChatbotRankingProvider: React.FC<ChatbotRankingProviderProps> = ({
  children,
  useChatbotV2,
  lintErrors,
  assignmentId = uuid.v4(),
  useFairStreaming,
  isStudio,
  projectId,
  useEditPrompt,
  allowPastingInRLHFEditResponse,
  forceEditCompletionLevenshteinThreshold,
  metadata,
  perTurnCheck,
  useAutoCritique,
}) => {
  const [enabled, setEnabled] = useState(false);
  // for reviewing
  const [readOnly, setReadOnly] = useState(false);
  const [isReview, setIsReview] = useState(false);
  const [reviewNoBranching, setReviewNoBranching] = useState(false);
  const [interactionNumber, setInteractionNumber] = useState(-1);
  const [originalHistory, setOriginalHistory] = useState<ChatHistory>({ interactions: [] });

  const [transcript, setTranscript] = useState<string | undefined>('');
  const [history, setHistory] = useLocalStorage<ChatHistory>('chatHistory' + assignmentId, {
    interactions: [],
  });
  const [streamingInteraction, setStreamingInteraction] = useState<Interaction | undefined>(
    undefined,
  );
  const [additionalResponseData, setAdditionalResponseData] = useState<any>({});
  const [perResponseFields, setPerResponseFields] = useState<TextCollectionField[]>([]);
  const [perConversationFields, setPerConversationFields] = useState<TextCollectionField[]>([]);
  const [perTurnFields, setPerTurnFields] = useState<TextCollectionField[]>([]);
  const [fieldsToShow, setFieldsToShow] = useState<TextCollectionField[]>([]);
  const [rlhfParams, setRLHFParams] = useState<RLHFParams | undefined>(undefined);
  const [annotations, setAnnotations] = useState<Record<string, TextCollectionAnnotation>>({});
  const [chatBoxTexts, setChatBoxTexts] = useState<string[]>(['Type your prompt']);
  const [currentOptionIndicesForRanking, setCurrentOptionIndicesForRanking] = useLocalStorage<
    number[]
  >('currentOptionIndicesForRanking' + assignmentId, []);
  // Fair ask
  const [needEditBestCompletion, setNeedEditBestCompletion] = useState(false);
  const [editCompletionTemplate, setEditCompletionTemplate] = useState<TextField | undefined>(
    undefined,
  );
  const [autoSpecText, setAutoSpecText] = useState<string>('');
  const [autoSpecLoading, setAutoSpecLoading] = useState<boolean>(false);
  const [critiquing, setCritiquing] = useState<boolean>(false);
  const [critiqueResponseChanged, setCritiqueResponseChanged] = useState<number>(0);
  const [critiqueText, setCritiqueText] = useState<string>('');
  const [critiqueBlockSubmit, setCritiqueBlockSubmit] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<any[]>([]);
  // key is a blacklisted word and value is the mask work for that.
  const [blacklistedWords, setBlacklistedWords] = useState<Record<string, string> | undefined>(
    undefined,
  );
  const [hasFetchedBlacklistedWords, setHasFetchedBlacklistedWords] = useState(false);

  const [isStreaming, setIsStreaming] = useState(false);

  const { setLatestValue } = useAttemptBehaviorLog();

  const enableAutoCritique = useMemo(() => {
    return useAutoCritique || rlhfParams?.autoCritique;
  }, [useAutoCritique, rlhfParams]);

  // for ranged multi-turn
  const [conversationEndedEarly, setConversationEndedEarly] = useLocalStorage<any>(
    'conversationEndedEarly' + assignmentId,
    false,
  );

  const [spellingAndGrammarCheckEnabled, setSpellingAndGrammarCheckEnabled] = useState(false);
  // Templates

  // Add this field to overwrite the `edit_completion` template
  const EDIT_COMPLETION_TEMPLATE_FIELD_ID = 'editCompletionTemplate';

  const editCompletionField: TextField = {
    type: TextCollectionFieldType.Text,
    field_id: 'edit_completion',
    title: 'Edit the best chosen response',
    hint: 'Edit the response to more closely answer the prompt. If no changes necessary please leave as is',
    use_grammar_check: spellingAndGrammarCheckEnabled,
  };

  const editCompletionFieldWithMarkdownPreview: TextField = {
    type: TextCollectionFieldType.Text,
    field_id: 'edit_completion',
    title: 'Edit the chatbot response',
    hint: 'Edit the response to more closely answer the prompt. If no changes necessary please leave as is',
    use_grammar_check: spellingAndGrammarCheckEnabled,
    show_markdown_preview: true,
  };

  const editPromptField: TextField = {
    type: TextCollectionFieldType.Text,
    field_id: 'edit_prompt',
    title: 'Edit the prompt',
    use_grammar_check: spellingAndGrammarCheckEnabled,
  };
  // for benchmarks
  // TODO: check training attempt to see if its an attempter BM to enable isBenchmark
  const isBenchmark = rlhfParams?.rlhfBenchmarkType === RlhfBenchmarkType.BaseAttemptRanking;

  // v2 states
  const [selectedCompletion, setSelectedCompletionDirect] = useLocalStorage<any>(
    'selectedCompletion' + assignmentId,
    0,
  );
  const [selectedInteraction, setSelectedInteraction] = useLocalStorage<any>(
    'selectedInteraction' + assignmentId,
    -1,
  );

  useEffect(() => {
    // recover selectedInteraction in case user refreshes page during generation
    if (useChatbotV2) {
      let index = selectedInteraction;
      // if there is an interaction in history, you must at least be selecting the first interaction
      if (history.interactions.length) index = Math.max(0, index);
      // if the user refreshes during the second or later interaction, we need to increment to select the incomplete interaction
      if (
        history.interactions.length > index + 1 &&
        !!history.interactions[index].chosenCompletion &&
        !history.interactions[index + 1].chosenCompletion
      )
        index += 1;
      // we should never be selecting an interaction index that is not in the history
      setSelectedInteraction(Math.min(index, history.interactions.length - 1));
    }
  }, [useChatbotV2]);

  const [onPerConvoField, setOnPerConvoField] = useLocalStorage<any>(
    'onPerConvoField' + assignmentId,
    false,
  );
  const [overrideViewFields, setOverrideViewFields] = useLocalStorage<any>(
    'overrideViewFields' + assignmentId,
    false,
  );
  const [isMakingChanges, setIsMakingChanges] = React.useState(false); // if reviewer is making changes

  const [taskCustomer, setTaskCustomer] = useState<string | undefined>(undefined);

  const [candidateBestCompletion, setCandidateBestCompletion] = useLocalStorage<string | undefined>(
    'candidateBestCompletion' + assignmentId,
    undefined,
  );

  const [candidateEditedPrompt, setCandidateEditedPrompt] = useState<string | undefined>(undefined);

  const autoCritiqueIndex = useChatbotV2
    ? selectedInteraction + 1
    : reviewNoBranching
    ? interactionNumber + 1
    : history.interactions.length;
  const finalResponse: string = _.get(
    annotations,
    `editCompletion_${autoCritiqueIndex}.response[0]`,
    '',
  ) as string; // for autocritique

  // Fair logic
  const hasRankingResponse = !!(
    annotations?.ranking_multiple_chat_api_endpoints?.response &&
    annotations.ranking_multiple_chat_api_endpoints.response.length > 0 &&
    annotations.ranking_multiple_chat_api_endpoints.response[0] !== 'null'
  );

  // Flipped when the ranking is updated
  const [shouldUpdateBestCompletion, setShouldUpdateBestCompletion] = useState(false);

  const minNumberOfInteractions =
    rlhfParams?.rangedNumberOfInteractions?.min ?? rlhfParams?.numberOfInteractions ?? 1;
  const maxNumberOfInteractions =
    rlhfParams?.rangedNumberOfInteractions?.max ?? rlhfParams?.numberOfInteractions ?? 1;

  const handleSetCandidateBestCompletion = useCallback(
    (text: string | undefined) => {
      setCandidateBestCompletion(text?.trim());

      if (text) {
        const index = reviewNoBranching
          ? Math.max(interactionNumber, 0)
          : history.interactions.length - 1;

        setLatestValue({
          fieldId: `editCompletion_${index + 1}`,
          text: text?.trim() || '',
          overrideForField: true, // this resets the tracking for the field for behavior log
        });
      }
    },
    [
      history.interactions.length,
      interactionNumber,
      reviewNoBranching,
      setCandidateBestCompletion,
      setLatestValue,
    ],
  );

  useEffect(() => {
    setShouldUpdateBestCompletion(true);
  }, [
    annotations?.ranking_multiple_chat_api_endpoints?.response?.[0],
    annotations?.[`ranking_${history.interactions.length}`]?.response?.[0],
  ]);

  const maskBlacklistedWords = (text: string, correction?: Record<string, string>) => {
    if (!correction || Object.keys(correction).length === 0) {
      return text;
    }
    const reg = new RegExp(Object.keys(correction).join('|'), 'g');
    return text.replace(reg, matched => correction[matched]);
  };

  useEffect(() => {
    if (isReview) {
      setInteractionNumber(0);
    }
  }, [isReview]);

  useEffect(() => {
    async function fetchData() {
      setHasFetchedBlacklistedWords(true);
      const response: Record<
        string,
        Record<string, string>
      > = await API.selfserve.fetchRlhfBlacklistWords();
      setBlacklistedWords(response?.flamingo);
    }
    if (!rlhfParams?.isFair || hasFetchedBlacklistedWords) {
      return;
    } else {
      fetchData();
    }
  }, [blacklistedWords, hasFetchedBlacklistedWords, rlhfParams?.isFair]);

  const setSelectedCompletion = (idx: number) => {
    if (isStreaming) return;

    // if there are no per interaction fields, we force it to go to the ranking selection
    if (perResponseFields?.length === 0) {
      setSelectedCompletionDirect(rlhfParams!.numberOfCompletions);
    } else {
      setSelectedCompletionDirect(idx);
    }
  };

  useEffect(() => {
    async function execute() {
      if (projectId) {
        const { enabled } = await API.selfserve.fetchSpellingAndGrammarEnabled({
          projectId,
        });
        setSpellingAndGrammarCheckEnabled(enabled);
      }
    }
    execute();
  }, [projectId]);

  const isDone = useMemo(() => {
    if (isStreaming) {
      return false;
    }

    if (
      enableAutoCritique &&
      (critiqueBlockSubmit || (!critiqueText.length && rlhfParams?.numberOfInteractions === 1)) &&
      !isReview &&
      !overrideViewFields // if attempter is 'reviewing' their attempt, it is like review mode and isDone is still true
    ) {
      return false;
    }

    const interactions = history.interactions;
    if (readOnly || reviewNoBranching) return interactionNumber === interactions.length;

    if (conversationEndedEarly) return true;

    if (
      maxNumberOfInteractions === 1 &&
      interactions.length === 1 &&
      perConversationFields.length === 0
    ) {
      return true;
    }

    const lastChosenCompletion =
      interactions.length === 0 ? false : interactions[interactions.length - 1].chosenCompletion;

    const appropriateLengthOfInteractions = isReview
      ? interactions.length >= minNumberOfInteractions
      : interactions.length >= maxNumberOfInteractions;
    return !!(rlhfParams && appropriateLengthOfInteractions && lastChosenCompletion);
  }, [
    isStreaming,
    rlhfParams,
    critiqueBlockSubmit,
    critiqueText.length,
    isReview,
    overrideViewFields,
    history.interactions,
    readOnly,
    reviewNoBranching,
    interactionNumber,
    conversationEndedEarly,
    minNumberOfInteractions,
    maxNumberOfInteractions,
    perConversationFields.length,
    enableAutoCritique,
  ]);

  const isRangedMultiTurn = useMemo(() => {
    return !!rlhfParams?.rangedNumberOfInteractions;
  }, [rlhfParams?.rangedNumberOfInteractions]);

  const resetLastInteraction = useCallback(():
    | Record<string, TextCollectionAnnotation>
    | undefined => {
    try {
      const idx = history.interactions.length - 1;
      const _annotations = _.cloneDeep(annotations);
      for (const field of Object.keys(_annotations)) {
        const tokens = field.split('_');
        let interactionIdx;
        if (
          tokens.length > 2 &&
          parseInt(tokens[tokens.length - 2]) &&
          parseInt(tokens[tokens.length - 1])
        ) {
          // assume it's a per response field (ex: ratings)
          interactionIdx = parseInt(tokens[tokens.length - 2]) - 1;
        } else {
          // else it's a per interaction field (ex: rankings) or per conversation field (in which case parseInt will return NaN)
          interactionIdx = parseInt(tokens[tokens.length - 1]) - 1;
        }
        if (idx === interactionIdx) {
          delete _annotations[field];
        }
      }
      return _annotations;
    } catch (e) {
      // do nothing
    }
  }, [annotations, history.interactions.length]);

  const handleUndo = useCallback(() => {
    setCandidateBestCompletion(undefined);
    setHistory(prevHistory => ({
      ...prevHistory,
      interactions: prevHistory.interactions.slice(0, prevHistory.interactions.length - 1),
    }));
    setSelectedCompletion(0);
    if (useChatbotV2) {
      // history.interactions.length - 1 represents the last interaction,
      // but subtract an additional 1 since the history just shrunk
      setSelectedInteraction(Math.max(-1, history.interactions.length - 2));
    }
  }, [
    setCandidateBestCompletion,
    history.interactions.length,
    setHistory,
    useChatbotV2,
    setSelectedInteraction,
    setSelectedCompletion,
  ]);

  const [abortController, setAbortController] = useState<AbortController>(new AbortController());

  const stopStreaming = useCallback(() => {
    abortController.abort();
  }, [abortController]);

  const rangedMultiTurnEndConversation = useCallback(() => {
    setConversationEndedEarly(true);
    setSelectedInteraction(history.interactions.length);
  }, [setConversationEndedEarly, setSelectedInteraction, history.interactions.length]);

  const rangedMultiTurnCanSubmit = useMemo(() => {
    const interactions = history.interactions;
    const lastChosenCompletion =
      interactions.length === 0 ? false : interactions[interactions.length - 1].chosenCompletion;
    return !!(
      rlhfParams?.rangedNumberOfInteractions &&
      interactions.length >= minNumberOfInteractions &&
      lastChosenCompletion &&
      fieldsToShow.length === 0
    );
  }, [
    history.interactions,
    rlhfParams?.rangedNumberOfInteractions,
    minNumberOfInteractions,
    fieldsToShow.length,
  ]);

  // for chatbot v2 edit response and edit prompt
  // since these edits do not interact with the Save and Continue button
  // we need to make updates to the history as the fields are updated instead of when the button is clicked
  useEffect(() => {
    // currently only on review level
    if (useChatbotV2 && overrideViewFields && (useEditPrompt || rlhfParams?.editBestCompletion)) {
      if (useEditPrompt) {
        const newEditPrompt = _.get(
          annotations as any,
          `editPrompt_${selectedInteraction + 1}.response[0]`,
          undefined,
        );
        const currPrompt = history.interactions[selectedInteraction]?.prompt;
        if (currPrompt && newEditPrompt && newEditPrompt !== currPrompt) {
          const _history = _.cloneDeep(history);
          _history.interactions[selectedInteraction].prompt = newEditPrompt;
          setHistory(_history);
        }
      }

      if (rlhfParams?.editBestCompletion) {
        const newResponse = _.get(
          annotations as any,
          `editCompletion_${selectedInteraction + 1}.response[0]`,
          undefined,
        );
        const currInteraction = history.interactions[selectedInteraction];
        const currResponse =
          currInteraction?.displayedCompletion?.text || currInteraction?.chosenCompletion?.text;
        if (currResponse && newResponse && newResponse !== currResponse) {
          const _history = _.cloneDeep(history);
          if (_history.interactions[selectedInteraction]?.displayedCompletion !== undefined) {
            _history.interactions[selectedInteraction].displayedCompletion!.text = newResponse;
          } else {
            _history.interactions[selectedInteraction].displayedCompletion = {
              text: newResponse,
              isEdited: true,
            };
          }

          if (
            rlhfParams?.useEditedResponseInHistory &&
            _history.interactions[selectedInteraction].chosenCompletion
          ) {
            _history.interactions[selectedInteraction].chosenCompletion!.text = newResponse;
            _history.interactions[selectedInteraction].chosenCompletion!.isEdited = true;
          }
          setHistory(_history);
        }
      }
    }
  }, [annotations]);

  const hideNextButton = useMemo(() => {
    if (useChatbotV2) {
      return !(fieldsToShow.length > 0 && !(isDone || isMakingChanges));
    }
    // FAIR ask, optimization to not require "next" after answering the form
    // when there is only 1 interaction
    if (rlhfParams?.isFair && !isRangedMultiTurn) {
      return true;
    }
    if (isRangedMultiTurn && isReview) return true;
    const interactions = history.interactions;
    if (
      maxNumberOfInteractions === 1 &&
      interactions.length === 1 &&
      perConversationFields.length === 0
    ) {
      return true;
    }
    return false;
  }, [
    useChatbotV2,
    rlhfParams?.isFair,
    isRangedMultiTurn,
    isReview,
    history.interactions,
    maxNumberOfInteractions,
    perConversationFields.length,
    fieldsToShow.length,
    isDone,
    isMakingChanges,
  ]);

  const cohereSkipLongPrompt = useUnleashFeatureFlag(
    'cohere-skip-long-prompt',
    { projectId },
    false,
  );

  const cohereIncludePreambleAndSeed = useUnleashFeatureFlag(
    'cohere-include-preamble-and-seed',
    { projectId },
    false,
  );

  const cohereEnableStreaming = useUnleashFeatureFlag(
    'crab-enabled-streaming',
    { projectId },
    false,
  );

  const nextButtonFrontendLintEnabled = useUnleashFeatureFlag(
    'enable-rlhf-next-button-frontend-lint',
    { projectId },
    false,
  );

  const forceEditCompletion = useUnleashFeatureFlag(
    'rlhf-force-edit-completion',
    { projectId },
    false,
  );

  const skipAutoCritiqueFormatCheck = useUnleashFeatureFlag(
    'skip-auto-critique-format-check',
    { projectId },
    false,
  );

  const enableModelResponseComparisonModal = useUnleashFeatureFlag(
    'enable-model-response-comparison-modal',
    { projectId },
    false,
  );

  const canResetChat: boolean =
    useUnleashFeatureFlag('rlhf-chatbot-view-can-reset', { projectId }, false) || false;

  const enableStreaming =
    cohereEnableStreaming ||
    useFairStreaming ||
    _.get(rlhfParams, 'chatApiAdditionalParams.stream', false) ||
    (_.get(rlhfParams, 'chatApiEndpoints', []).length > 0 &&
      _.every(_.get(rlhfParams, 'chatApiEndpoints'), endpoint => endpoint.params?.stream));

  const alpacaMultiturn = useUnleashFeatureFlag('alpaca-multiturn', { projectId }, false);

  const optionNoun = alpacaMultiturn ? 'Answer' : 'Option';

  const getTopCompletionIndex = useCallback(
    (rankingQuestionIndex: number) => {
      if (!rlhfParams) return 0;
      const _topCompletionIndex = _.get(
        annotations as any,
        `ranking_${rankingQuestionIndex}.response[0]`,
        '0',
      ).split(',')[0] as number;
      if (_topCompletionIndex) return _topCompletionIndex;
      return 0;
    },
    [annotations, rlhfParams],
  );

  const checkIfItemBlank = useCallback(
    (_interaction: number, _completion: number) => {
      if (rlhfParams?.numberOfCompletions && _completion === rlhfParams!.numberOfCompletions) {
        if (rlhfParams?.numberOfCompletions === 1 && rlhfParams?.editBestCompletion) {
          const id = `editCompletion_${_interaction + 1}`;
          if (!_.size(annotations[id]?.response) || isBlankField(annotations[id])) {
            return true;
          }
          return false;
        }

        // on ranking question
        const index = _interaction;
        const lastInteraction = history.interactions[index];

        if (lastInteraction === undefined) return false;
        const rankingQuestionIndex = index + 1;

        // something needs to be ranked in first top position
        if (_.get(annotations as any, `ranking_${rankingQuestionIndex}.response[0]`) === 'null')
          return true;

        // count number of ranked items
        // eg for ties, response could be ["0,1", "null"]
        const rankedItems = _.get(
          annotations as any,
          `ranking_${rankingQuestionIndex}.response`,
          [],
        )
          .join(',')
          .split(',')
          .filter((x: string) => x !== 'null').length;
        if (rankedItems < rlhfParams!.numberOfCompletions) {
          return true;
        } else if (perTurnFields && perTurnFields.length > 0) {
          // if there are per turn fields, we need to check if the per turn fields are blank
          for (const f of perTurnFields) {
            const interactionId = `${_interaction + 1}`;
            const id = `${f.field_id}_${interactionId}`;
            if (
              !!f.required &&
              (!_.size(annotations[id]?.response) || isBlankField(annotations[id]))
            ) {
              return true;
            }
          }
        }
        const topCompletionIndex = getTopCompletionIndex(rankingQuestionIndex);
        const chosenCompletion = lastInteraction.completions[topCompletionIndex];
        return chosenCompletion === undefined;
      } else {
        // on per response question
        for (const f of perResponseFields) {
          const completionId = `${_interaction + 1}_${_completion + 1}`;
          const id = `${f.field_id}_${completionId}`;
          if (
            !!f.required &&
            (!_.size(annotations[id]?.response) || isBlankField(annotations[id]))
          ) {
            return true;
          }
        }
      }
      return false;
    },
    [
      rlhfParams,
      history.interactions,
      annotations,
      getTopCompletionIndex,
      perResponseFields,
      perTurnFields,
    ],
  );

  const nextButtonDisabled: string | undefined = useMemo(() => {
    if (useChatbotV2) {
      if (isStreaming) return 'The model is still streaming.';

      // if we are on the ranking/edit completion page, we can only go next if all fields are complete
      if (
        selectedCompletion === rlhfParams?.numberOfCompletions &&
        perResponseFields &&
        perResponseFields.length > 0
      ) {
        for (let i = 0; i < selectedCompletion; i++) {
          if (checkIfItemBlank(selectedInteraction, i)) {
            return 'Please complete all required fields for all completions.';
          }
        }
      }

      const currentCompletionFieldsBlank =
        checkIfItemBlank(selectedInteraction, selectedCompletion) &&
        !(rlhfParams?.numberOfCompletions === 1 && selectedCompletion === 1); // edge case: 1 completion, no ranking field.

      if (currentCompletionFieldsBlank) return 'Please complete all required fields to continue.';
    }

    if (forceEditCompletion) {
      for (const field of fieldsToShow) {
        if (field.field_id.startsWith('editCompletion_')) {
          const interactionIndex = parseInt(field.field_id.split('_')[1]) - 1;
          const value = (
            _.get(annotations, `${field.field_id}.response[0]`, '') as unknown as string
          ).trim();

          const originalCompletion =
            history.interactions[interactionIndex]?.completions[0]?.text.trim();

          if (originalCompletion) {
            if (
              forceEditCompletionLevenshteinThreshold !== undefined &&
              value &&
              originalCompletion
            ) {
              const difference = relativeLevenshteinDistance(originalCompletion, value);
              if (difference < forceEditCompletionLevenshteinThreshold) {
                return `Your response is too similar to the current response.`;
              }
            } else if (value === originalCompletion) {
              return 'You must provide an edit to the response.';
            }
            if (enableAutoCritique && !critiqueText.length) {
              return 'Please get a critique before continuing.';
            }
          }
        }
      }
    }
    if (nextButtonFrontendLintEnabled) {
      if (
        _.some(fieldsToShow, field => {
          const fieldId = field.field_id;
          const value = _.get(annotations, `${fieldId}.response`);
          if (!value) return true;
          if (field.type === TextCollectionFieldType.RankingOrder) {
            const numOptions = field.num_options;
            const numFilled = (value as unknown as string[]).filter(
              x => x !== 'null' && x !== '',
            ).length;
            return numFilled !== numOptions;
          }
          return (
            value === undefined ||
            (_.isArray(value) &&
              (_.flatten(value).length === 0 ||
                (value.length === 1 && _.isString(value[0]) && value[0].length === 0))) ||
            (_.isString(value) && value.length === 0)
          );
        })
      ) {
        return 'Please answer all questions.';
      }
    }

    return undefined;
  }, [
    useChatbotV2,
    forceEditCompletion,
    nextButtonFrontendLintEnabled,
    isStreaming,
    selectedCompletion,
    rlhfParams,
    perResponseFields,
    checkIfItemBlank,
    selectedInteraction,
    fieldsToShow,
    annotations,
    history.interactions,
    forceEditCompletionLevenshteinThreshold,
    critiqueText.length,
    enableAutoCritique,
  ]);

  const formatSections = (sections: JSX.Element[]) => {
    if (isStreaming) {
      return [
        <div key="default" style={{ marginLeft: '8px' }}>
          <h3>Please wait, the model is generating responses. </h3>
          {!!autoSpecText.length && (
            <div
              style={{
                margin: '0 8px',
                padding: 8,
                whiteSpace: 'pre-wrap',
                backgroundColor: 'rgba(144, 202, 249, .2)',
                borderRadius: 8,
              }}
            >
              {autoSpecText}
            </div>
          )}
        </div>,
      ];
    }

    if (useChatbotV2) {
      return formatSectionsV2(sections);
    }

    if (rlhfParams?.isFair) {
      sections = formatSectionsFair(sections);
      if (enableAutoCritique) {
        sections = formatSectionsAutoCritique(sections);
      }
      return sections;
    }

    const interactions = history.interactions;

    if (sections.length === 0) {
      return [
        <div key="default" style={{ marginLeft: '8px' }}>
          <h3>Please type a prompt in the left textbox</h3>
        </div>,
      ];
    }

    if (perResponseFields.length > 0 && !isDone) {
      let numberOfCompletions = rlhfParams!.numberOfCompletions;
      if (rlhfParams?.chatApiMultiEndpoints && rlhfParams?.chatApiMultiEndpoints.length > 0) {
        numberOfCompletions = rlhfParams?.chatApiMultiEndpoints.length;
      }
      for (let i = 0; i < numberOfCompletions; i++) {
        const optionIndex = i + 1;
        const spliceIndex = i * perResponseFields.length + i;
        i === 0
          ? sections.splice(
              spliceIndex,
              0,
              <div>
                <h3>
                  Answer the following questions about {optionNoun}{' '}
                  {alpacaMultiturn ? interactions.length : optionIndex}:
                </h3>
              </div>,
            )
          : sections.splice(
              spliceIndex,
              0,
              <div>
                <br></br>
                <br></br>
                <hr></hr>
                <h3>
                  Answer the following questions about {optionNoun}{' '}
                  {alpacaMultiturn ? interactions.length : optionIndex}:
                </h3>
              </div>,
            );
      }
      if (rlhfParams && rlhfParams.numberOfCompletions > 1) {
        // separate the ranking question, which is only displayed when there are multiple completions
        sections.splice(
          sections.length - 1,
          0,
          <div>
            <br></br>
            <br></br>
            <hr></hr>
          </div>,
        );
      }
    }
    if (rlhfParams && enableAutoCritique) {
      sections = formatSectionsAutoCritique(sections);
    }

    return sections;
  };

  const formatSectionsFair = (sections: JSX.Element[]) => {
    if (sections.length === 0)
      return [
        <div key="default" style={{ marginLeft: '8px' }}>
          <h3>Please type a prompt in the left textbox</h3>
        </div>,
      ];

    if (
      history.interactions.length > 0 &&
      ((rlhfParams?.chatApiMultiEndpoints || []).length !==
        history.interactions[history.interactions.length - 1].completions.length ||
        _.some(
          history.interactions[history.interactions.length - 1].completions,
          completion => !completion.text,
        ))
    ) {
      return [
        <div key="default" style={{ marginLeft: '8px' }}>
          <h3>
            There was an issue with generating the full response, please undo or refresh the page.{' '}
          </h3>
        </div>,
      ];
    }
    sections.splice(
      0,
      0,
      <div>
        <h3>Rank your preferences between the following options:</h3>
        {!!autoSpecText.length && (
          <div
            style={{
              margin: '0 8px',
              padding: 8,
              whiteSpace: 'pre-wrap',
              backgroundColor: 'rgba(144, 202, 249, .2)',
              borderRadius: 8,
            }}
          >
            {autoSpecText}
          </div>
        )}
      </div>,
    );

    if (rlhfParams && rlhfParams.numberOfCompletions > 1) {
      // separate the ranking question, which is only displayed when there are multiple completions
      sections.splice(
        sections.length - 1,
        0,
        <div>
          <br></br>
          <br></br>
          <hr></hr>
        </div>,
      );
    }

    return sections;
  };

  const formatSectionsAutoCritique = (sections: JSX.Element[]) => {
    if (finalResponse.length && enableAutoCritique) {
      sections.splice(
        sections.length,
        0,
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
          <Button
            disabled={critiquing || !critiqueResponseChanged}
            onClick={getCritique}
            style={{ marginLeft: 'auto', marginRight: 16 }}
          >
            {critiquing ? 'Critiquing...' : 'Get Critique'}
          </Button>
        </div>,
      );
    }
    if (enableAutoCritique && critiqueText.length) {
      const critical =
        critiqueText.toLowerCase().startsWith('severity: critical') ||
        critiqueText.toLowerCase().startsWith('severity: major');
      const correctFormat = critiqueText.toLowerCase().startsWith('severity');
      const rateLimitError = critiqueText.toLowerCase().includes('rate limit exceeded');

      sections.splice(
        sections.length - 1,
        0,
        <div
          style={{
            margin: '0 8px 8px 8px',
            padding: 8,
            whiteSpace: 'pre-wrap',
            backgroundColor: critical ? 'rgba(229, 115, 115, .2)' : 'rgba(255, 183, 77, .2)',
            borderRadius: 8,
          }}
        >
          {rateLimitError
            ? 'Critique unavailable due to too many concurrent requests'
            : correctFormat || skipAutoCritiqueFormatCheck
            ? critiqueText
            : 'Could not analyze answer, please make an edit and try again.'}
        </div>,
      );
    }
    return sections;
  };

  const formatSectionsV2 = (sections: JSX.Element[]) => {
    if (onPerConvoField && sections.length > 0) {
      sections.splice(
        0,
        0,
        <div>
          <h3>Answer the following questions about the entire conversation:</h3>
        </div>,
      );

      return sections;
    }

    if (isDone && sections.length === 0) return sections;

    if (sections.length === 0)
      return [
        <div key="default" style={{ marginLeft: '8px' }}>
          <h3>
            {rangedMultiTurnCanSubmit && !conversationEndedEarly
              ? 'Enter a prompt to continue or end the conversation now.'
              : 'Enter a prompt to continue.'}
          </h3>
        </div>,
      ];

    if (
      perTurnFields.length > 0 &&
      rlhfParams!.numberOfCompletions > 1 &&
      selectedCompletion > rlhfParams!.numberOfCompletions - 1
    ) {
      sections.splice(
        0,
        0,
        <div>
          <h3>Answer the following questions about this turn:</h3>
        </div>,
      );
      return sections;
    }

    if (perResponseFields.length > 0 && selectedCompletion < rlhfParams!.numberOfCompletions) {
      const i = selectedCompletion;
      const optionIndex = i + 1;
      sections.splice(
        0,
        0,
        <div>
          <h3>Answer the following questions about option {optionIndex}:</h3>
        </div>,
      );
    } else if (rlhfParams && enableAutoCritique) {
      sections = formatSectionsAutoCritique(sections);
    }

    return sections;
  };

  const setTaxonomy = (
    fields: TextCollectionField[],
    perConversationFieldIds: string[],
    perTurnFieldIds?: string[],
  ) => {
    setEditCompletionTemplate(
      fields.find(
        f =>
          f.field_id === EDIT_COMPLETION_TEMPLATE_FIELD_ID &&
          f.type === TextCollectionFieldType.Text,
      ) as TextField | undefined,
    );
    fields = fields.filter(f => f.field_id !== EDIT_COMPLETION_TEMPLATE_FIELD_ID);
    setPerResponseFields(
      fields.filter(
        f =>
          !perTurnFieldIds?.includes(f.field_id) && !perConversationFieldIds.includes(f.field_id),
      ),
    );
    setPerTurnFields(fields.filter(f => perTurnFieldIds?.includes(f.field_id)));
    setPerConversationFields(fields.filter(f => perConversationFieldIds.includes(f.field_id)));
  };

  const codeFields = useMemo(() => {
    return perResponseFields.filter(f => f.type === TextCollectionFieldType.Code);
  }, [perResponseFields]);

  const setReadOnlyWrapper = (newReadOnly: boolean) => {
    if (useChatbotV2) {
      if (newReadOnly && selectedInteraction < 0) {
        setSelectedInteraction(0);
      }
      if (readOnly && !newReadOnly) {
        // user clicked "make changes" while reviewing
        const _newHistory = {
          ...history,
        };
        if (!onPerConvoField) setOverrideViewFields(true);
        // triggers rechecking isDone
        setHistory(_newHistory);
        setIsMakingChanges(true);
      } else if (!readOnly && newReadOnly) {
        // user canceled "make changes", reset original chat history
        setHistory(originalHistory);
        setIsMakingChanges(false);
      }
      setReadOnly(newReadOnly);
      return;
    }

    if (readOnly && !newReadOnly) {
      // user clicked "make changes" while reviewing
      setReviewNoBranching(true);
    } else if (!readOnly && newReadOnly) {
      // user canceled "make changes", reset original chat history
      setHistory(originalHistory);
      // go directly to first interaction if available
      setInteractionNumber(0);
      setReviewNoBranching(false);
    }
    setReadOnly(newReadOnly);
  };

  const startBranchConversation = () => {
    if ((!useChatbotV2 && !readOnly && reviewNoBranching) || (useChatbotV2 && isMakingChanges)) {
      setReviewNoBranching(false);
      const _newHistory = {
        ...history,
        interactions: history.interactions.slice(
          0,
          useChatbotV2 ? selectedInteraction + 1 : interactionNumber + 1,
        ),
      };
      const lastInteraction = _newHistory.interactions[_newHistory.interactions.length - 1];
      _newHistory.interactions[_newHistory.interactions.length - 1] = {
        ...lastInteraction,
        chosenCompletion: undefined,
        displayedCompletion: undefined,
      };
      if (useChatbotV2) {
        setIsMakingChanges(false);
        setOverrideViewFields(false);
      }
      setHistory(_newHistory);
    }
  };

  useEffect(() => {
    if (readOnly && history.interactions.length === 0 && annotations.rlhf_history) {
      const _history = annotations.rlhf_history.response[0] as any;
      // edge case: if using edit prompts and we only have 1 interaction, we have to make sure the edited prompt is shown in UI
      if (useEditPrompt && _history.interactions.length === 1) {
        const editedPrompt = useEditPrompt
          ? _.get(annotations as any, `editPrompt_1.response[0]`, undefined)
          : undefined;
        if (editedPrompt) {
          _history.prompt = editedPrompt;
          _history.interactions[0].prompt = editedPrompt;
        }
      }
      setHistory(_history);
      setOriginalHistory(_history);
    }
  }, [readOnly, annotations]);

  useEffect(() => {
    if (rlhfParams?.editBestCompletion && !rlhfParams?.isFair && history.interactions.length > 0) {
      const index = reviewNoBranching
        ? Math.max(interactionNumber, 0)
        : history.interactions.length - 1;
      const lastInteraction = history.interactions[index];
      if (rlhfParams!.numberOfCompletions === 1) {
        // autopopulate candidate completion if theres only 1 option
        let editedCompletionToUse = lastInteraction.completions[0]?.text;
        // if there is already an edited completion (i.e. on review level), we use that instead
        const existingCompletion = _.get(
          annotations as any,
          `editCompletion_${index + 1}.response[0]`,
          undefined,
        );
        if (existingCompletion && existingCompletion !== '') {
          editedCompletionToUse = existingCompletion;
        }
        setCandidateBestCompletion(editedCompletionToUse);
        setLatestValue({
          fieldId: `editCompletion_${index + 1}`,
          text: editedCompletionToUse,
        });
      } else {
        const rankingQuestionIndex = index + 1;
        const topCompletionIndex = getTopCompletionIndex(rankingQuestionIndex);
        const tempBestCompletion = topCompletionIndex
          ? lastInteraction.completions[+topCompletionIndex]?.text
          : undefined;

        if (candidateBestCompletion !== tempBestCompletion) {
          handleSetCandidateBestCompletion(tempBestCompletion);
        }
      }
    }
    if (rlhfParams?.editBestCompletion && rlhfParams?.isFair) {
      const index = history.interactions.length - 1;
      const lastInteraction = history.interactions[index];
      const topCompletionIndex = annotations?.ranking_multiple_chat_api_endpoints?.response[0];
      const tempBestCompletion =
        topCompletionIndex !== null
          ? lastInteraction?.completions[parseInt(topCompletionIndex as string)]?.text
          : undefined;
      if (candidateBestCompletion !== tempBestCompletion) {
        handleSetCandidateBestCompletion(tempBestCompletion);
      }
      if (enableAutoCritique && hasRankingResponse) {
        setNeedEditBestCompletion(true);
      } else {
        setNeedEditBestCompletion(false);
      }
    }
  }, [
    annotations,
    interactionNumber,
    history.interactions,
    hasRankingResponse,
    enableAutoCritique,
  ]);

  useEffect(() => {
    if (useChatbotV2 && useEditPrompt && isMakingChanges) {
      const index = selectedInteraction;
      if (selectedInteraction < history.interactions.length && selectedInteraction >= 0) {
        const lastInteraction = history.interactions[index];
        setCandidateEditedPrompt(lastInteraction.prompt);
      }
    } else if (!useChatbotV2 && useEditPrompt && reviewNoBranching) {
      const index = interactionNumber;
      if (interactionNumber < history.interactions.length && interactionNumber >= 0) {
        const lastInteraction = history.interactions[index];
        setCandidateEditedPrompt(lastInteraction.prompt);
      }
    }
  }, [
    useEditPrompt,
    reviewNoBranching,
    interactionNumber,
    isMakingChanges,
    selectedInteraction,
    selectedCompletion,
  ]);

  const disablePastingInEditResponse = !isStudio && !allowPastingInRLHFEditResponse;

  const skipPromptValidation = !!isStudio;

  const createNewField = (f: TextCollectionField, completionId: string) => {
    const newField = {
      ...f,
      conditions: f.conditions?.map((c: NLPLintCondition) => {
        return Object.keys(c).reduce((acc, fieldId) => {
          acc[`${fieldId}_${completionId}`] = c[fieldId];
          return acc;
        }, {} as NLPLintCondition);
      }),
      field_id: `${f.field_id}_${completionId}`,
    };
    return newField;
  }; //

  // temp solution to set citationReferenceIndex
  // example task id: 64f1161b9eaac533f8a09f51
  // the task has one queryable_text_and_results field and a field_set of queryable_text_and_results as additional field
  useEffect(() => {
    const _annotations = annotations;
    for (const key in _annotations) {
      const obj = _annotations[key];
      if (obj.type === 'queryable_text_and_results' && obj?.response?.[0]) {
        obj.response[0].citationReferenceIndex = 1;
      }
      if (
        obj.type === 'field_set' &&
        obj?.response?.[0]?.additional_google_search?.type === 'queryable_text_and_results'
      ) {
        for (let i = 0; i < obj.response.length; i++) {
          const entry = obj?.response?.[i]?.additional_google_search
            ?.response?.[0] as QueryableTextAndResultsAnnotationEntry;
          if (entry) {
            entry.citationReferenceIndex = i + 2;
          }
        }
      }
    }
    setAnnotations(_annotations);
  }, [annotations]);

  // v1 useEffect
  useEffect(() => {
    if (useChatbotV2 || !rlhfParams || rlhfParams?.isFair) {
      return;
    }
    const interactions =
      readOnly || reviewNoBranching
        ? history.interactions.slice(0, interactionNumber + 1)
        : history.interactions;

    if (interactions.length === 0) {
      setTranscript(undefined);
      setFieldsToShow([]);
      return;
    }

    const lastInteraction = streamingInteraction ?? interactions[interactions.length - 1];

    const showForm =
      !lastInteraction.chosenCompletion || ((readOnly || reviewNoBranching) && !isDone);
    const transcriptSections = [];

    const end = showForm ? interactions.length - 1 : interactions.length;
    for (const interaction of interactions.slice(0, end)) {
      const botText = interaction?.displayedCompletion?.text || interaction.chosenCompletion?.text;
      if (!botText || _.isNil(botText)) {
        continue;
      }
      transcriptSections.push(`**You**: ${interaction.prompt} \n`);
      transcriptSections.push(`**Bot**: ${botText} \n`);
      transcriptSections.push(`*** \n`);
    }

    if (showForm) {
      transcriptSections.push(`**You**: ${lastInteraction.prompt} \n\n`);
      if (!alpacaMultiturn) {
        transcriptSections.push(
          `**Please evaluate the following options by answering the questions on the right:** \n\n`,
        );
      }

      const interactionNumberKey = interactions.length;
      const _fieldsToShow: TextCollectionField[] = [];

      let completionIndex = 1;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (const c of lastInteraction.completions) {
        if (currentOptionIndicesForRanking && currentOptionIndicesForRanking.length > 0) {
          if (!currentOptionIndicesForRanking.includes(completionIndex - 1)) {
            completionIndex++;
            continue;
          }
        }
        const text =
          c.model === 'writeyourown' ? `<span style="color:red">${c.text}</span>` : c.text;
        transcriptSections.push(
          `**${optionNoun} ${alpacaMultiturn ? interactionNumberKey : completionIndex}**:\n`,
        );
        transcriptSections.push(`\n${text.length ? text : '(response missing)'}\n`);
        transcriptSections.push(`\n`);

        const completionId = `${interactionNumberKey}_${completionIndex}`;
        for (const f of perResponseFields) {
          _fieldsToShow.push(createNewField(f, completionId));
        }
        completionIndex++;
      }
      const numberOfCompletions =
        rlhfParams.chatApiMultiEndpoints?.length || rlhfParams.numberOfCompletions;
      if (numberOfCompletions > 1) {
        _fieldsToShow.push({
          ...rankingField,
          num_items_to_rank: numberOfCompletions,
          num_options: numberOfCompletions,
          field_id: `ranking_${interactionNumberKey}`,
          max_tied_items: rlhfParams.maxRankingTies,
          hint:
            rlhfParams?.flamingoPipelineVersion === 'pipeline_v2'
              ? pipelineV2RankingHint
              : rankingField.hint,
        });
      }

      const writeYourOwn = lastInteraction?.completions?.[0]?.model === 'writeyourown';

      const editCompletionFieldTemplate = editCompletionTemplate ?? editCompletionField;

      const editBestCompletionField = writeYourOwn
        ? writeYourOwnCompletionField
        : editCompletionFieldTemplate;

      if (rlhfParams.editBestCompletion) {
        if (alpacaMultiturn) {
          _fieldsToShow.unshift({
            ...editBestCompletionField,
            disable_pasting: disablePastingInEditResponse,
            field_id: `editCompletion_${interactionNumberKey}`,
          });
        } else if (codeFields && codeFields.length > 0) {
          editCompletionFieldWithMarkdownPreview.title = `Edit completion: use {{code_${interactionNumberKey}}} to reference code`;
          _fieldsToShow.push({
            ...editCompletionFieldWithMarkdownPreview,
            disable_pasting: disablePastingInEditResponse,
            field_id: `editCompletion_${interactionNumberKey}`,
          });
        } else {
          _fieldsToShow.push({
            ...editBestCompletionField,
            disable_pasting: disablePastingInEditResponse,
            field_id: `editCompletion_${interactionNumberKey}`,
            show_markdown_preview: !!rlhfParams?.showMarkdownViewForEditCompletion,
          });
        }
      }

      if (useEditPrompt && reviewNoBranching) {
        _fieldsToShow.push({
          ...editPromptField,
          disable_pasting: disablePastingInEditResponse,
          field_id: `editPrompt_${interactionNumberKey}`,
        });
      }
      if (isStreaming) {
        setFieldsToShow([]);
      } else {
        setFieldsToShow(_fieldsToShow);
      }
    } else if (isDone) {
      transcriptSections.push(`*All done! Great work!* \n`);
      setFieldsToShow(perConversationFields);
    } else {
      if (rangedMultiTurnCanSubmit && !conversationEndedEarly) {
        transcriptSections.push(`*Type a prompt to continue or end the conversation now.* \n`);
      } else {
        transcriptSections.push(`*Type your next prompt* \n`);
      }
      setFieldsToShow([]);
    }

    const _transcript = transcriptSections.map(s => `${MARKDOWN_DIVIDER}\n ${s}`).join('');
    setTranscript(_transcript);
  }, [
    history,
    interactionNumber,
    isDone,
    rlhfParams,
    reviewNoBranching,
    isStreaming,
    conversationEndedEarly,
    streamingInteraction,
    useChatbotV2,
  ]);

  // Fair useEffect
  useEffect(() => {
    // Need to fetch blacklisted words first
    if (!rlhfParams?.isFair || !hasFetchedBlacklistedWords) {
      return;
    }

    let EmptyResponseMessaging = `(Model failed to generate a response, please either undo or refresh the page to try again)`;
    if (isStreaming) {
      EmptyResponseMessaging = `(Model is generating a response)`;
    }
    const interactions = readOnly
      ? history.interactions.slice(0, interactionNumber + 1)
      : history.interactions;

    if (interactions.length === 0) {
      setTranscript(undefined);
      setFieldsToShow([]);
      return;
    }
    const lastInteraction = streamingInteraction ?? interactions[interactions.length - 1];

    let _transcript = '';
    const showForm =
      !lastInteraction.chosenCompletion ||
      (readOnly && !isDone) ||
      rlhfParams?.flamingoPipelineVersion === 'pipeline_v2';

    const end = showForm ? interactions.length - 1 : interactions.length;

    for (const interaction of interactions.slice(0, end)) {
      const botText = interaction?.displayedCompletion?.text || interaction.chosenCompletion?.text;
      if (!botText || _.isNil(botText)) {
        continue;
      }
      _transcript += `**You**: ${interaction.prompt} \n`;
      _transcript += `**Bot**: ${botText} \n`;
      _transcript += '*** \n';
    }

    if (showForm) {
      _transcript += `**You**: ${lastInteraction.prompt} \n`;
      _transcript += `**Please evaluate the following options by answering the questions on the right:** \n\n`;
      const interactionNumberKey = useChatbotV2 ? selectedInteraction + 1 : interactions.length;
      let _fieldsToShow: TextCollectionField[] = [];
      // for approach 1 & 2, we just need to always place ranking question on the top.
      if (rlhfParams?.chatApiMultiEndpoints && rlhfParams?.chatApiMultiEndpoints.length > 0) {
        if (isRangedMultiTurn) {
          for (let i = 1; i < history.interactions[0].completions.length + 1; i++) {
            _fieldsToShow.push({
              ...fairOptionQualityFieldMultiTurn,
              title: `Please rate the quality of option ${i}`,
              field_id: `option_quality_${interactionNumberKey}_${i}`,
            });
          }
        }

        if (isRangedMultiTurn) {
          _fieldsToShow.push({
            ...fairRankingField,
            num_items_to_rank: history.interactions[0].completions.length,
            num_options: history.interactions[0].completions.length,
            field_id: `ranking_${interactionNumberKey}`,
            items_to_rank_candidates: currentOptionIndicesForRanking,
            is_review_mode: isReview,
            title: fairRankingField.title,
          });
        } else {
          _fieldsToShow.push({
            ...fairRankingField,
            num_items_to_rank: history.interactions[0].completions.length,
            num_options: history.interactions[0].completions.length,
            field_id: `ranking_multiple_chat_api_endpoints`,
            items_to_rank_candidates: currentOptionIndicesForRanking,
            is_review_mode: isReview,
            title: fairRankingField.title,
            hint:
              rlhfParams?.flamingoPipelineVersion === 'pipeline_v2'
                ? pipelineV2RankingHint
                : rankingField.hint,
          });
        }
      } else if (rlhfParams!.numberOfCompletions > 1) {
        _fieldsToShow.push({
          ...rankingField,
          num_items_to_rank: rlhfParams!.numberOfCompletions,
          num_options: rlhfParams!.numberOfCompletions,
          max_tied_items: rlhfParams.maxRankingTies,
          field_id: `ranking_${interactionNumberKey}`,
        });
      }
      let completionIndex = 1;
      for (const c of lastInteraction.completions) {
        if (currentOptionIndicesForRanking && currentOptionIndicesForRanking.length > 0) {
          if (!currentOptionIndicesForRanking.includes(completionIndex - 1)) {
            completionIndex++;
            continue;
          }
        }
        const originalText = c.text;
        const text = maskBlacklistedWords(originalText, blacklistedWords);
        _transcript += `**Option ${completionIndex}**:\n`;
        _transcript += `\n${text.length ? text : EmptyResponseMessaging}\n`;
        _transcript += '\n';

        completionIndex++;
      }

      if (readOnly) {
        const completionId = `${interactionNumberKey}_1`;
        if (rlhfParams?.flamingoPipelineVersion === 'pipeline_v2') {
          const fields: any[] = [];
          for (const f of perResponseFields) {
            fields.push(createNewField(f, completionId));
          }
          _fieldsToShow = [...fields, ..._fieldsToShow];
        } else if (isRangedMultiTurn) {
          // Skip
        } else {
          for (const f of perResponseFields) {
            _fieldsToShow.push(createNewField(f, completionId));
          }
        }
      }

      // if readonnly is true then without the !readonly check, we duplicate the fields to show
      if (!readOnly && rlhfParams?.flamingoPipelineVersion === 'pipeline_v2') {
        const completionId = `${interactionNumberKey}_1`;
        const fields: any[] = [];
        for (const f of perResponseFields) {
          fields.push(createNewField(f, completionId));
        }
        _fieldsToShow = [...fields, ..._fieldsToShow];
      }

      const writeYourOwn = lastInteraction?.completions?.[0]?.model === 'writeyourown';
      const editBestCompletionField = writeYourOwn
        ? writeYourOwnCompletionField
        : editCompletionField;

      if (rlhfParams!.editBestCompletion && needEditBestCompletion) {
        _fieldsToShow.push({
          ...editBestCompletionField,
          field_id: `editCompletion_${interactionNumberKey}`,
        });
      }
      setFieldsToShow(_fieldsToShow);
    } else if (isDone) {
      _transcript += '*All done! Great work!* \n';
      setFieldsToShow(perConversationFields);
    } else {
      const interactionNumberKey = interactions.length;
      if (isRangedMultiTurn && interactionNumberKey >= minNumberOfInteractions) {
        _transcript += '*Type your next prompt or complete the task* \n';
      } else {
        _transcript += '*Type your next prompt* \n';
      }
      setFieldsToShow([]);
    }
    setTranscript(_transcript);
  }, [
    history,
    interactionNumber,
    isDone,
    needEditBestCompletion,
    hasFetchedBlacklistedWords,
    autoSpecLoading,
    selectedInteraction,
    streamingInteraction,
  ]);

  // v2 useEffect
  useEffect(() => {
    if (!useChatbotV2) {
      return;
    }
    if (rlhfParams?.isFair) {
      return;
    }

    const interactions = history.interactions;

    if (interactions.length === 0) {
      setTranscript(undefined);
      setFieldsToShow([]);
      return;
    }

    const lastInteraction =
      interactions[Math.min(interactions.length - 1, Math.max(0, selectedInteraction))];

    const showForm =
      !lastInteraction.chosenCompletion ||
      (readOnly && !isDone) ||
      overrideViewFields ||
      rlhfParams?.flamingoPipelineVersion === 'pipeline_v2';
    // eslint-disable-next-line no-lonely-if
    if (onPerConvoField && !overrideViewFields) {
      setFieldsToShow(perConversationFields);
    } else if (showForm) {
      const interactionNumberKey = selectedInteraction + 1;
      const _fieldsToShow: TextCollectionField[] = [];

      const completionIndex = selectedCompletion + 1;
      if (!rlhfParams) return;
      if (completionIndex <= rlhfParams!.numberOfCompletions) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const completionId = `${interactionNumberKey}_${completionIndex}`;
        for (const f of perResponseFields) {
          _fieldsToShow.push(createNewField(f, completionId));
        }
      }

      if (
        rlhfParams!.numberOfCompletions >= 1 &&
        completionIndex > rlhfParams!.numberOfCompletions
      ) {
        if (rlhfParams!.numberOfCompletions > 1) {
          _fieldsToShow.push({
            ...rankingField,
            num_items_to_rank: rlhfParams!.numberOfCompletions,
            num_options: rlhfParams!.numberOfCompletions,
            max_tied_items: rlhfParams.maxRankingTies,
            field_id: `ranking_${interactionNumberKey}`,
          });
          for (const f of perTurnFields) {
            _fieldsToShow.push({ ...f, field_id: `${f.field_id}_${interactionNumberKey}` });
          }
        }

        if (rlhfParams!.editBestCompletion) {
          _fieldsToShow.push({
            ...editCompletionField,
            disable_pasting: disablePastingInEditResponse,
            field_id: `editCompletion_${interactionNumberKey}`,
            show_markdown_preview: !!rlhfParams?.showMarkdownViewForEditCompletion,
          });
        }

        if (overrideViewFields && isMakingChanges && useEditPrompt) {
          _fieldsToShow.push({
            ...editPromptField,
            disable_pasting: disablePastingInEditResponse,
            field_id: `editPrompt_${interactionNumberKey}`,
          });
        }
      }
      setFieldsToShow(_fieldsToShow);
    } else {
      setFieldsToShow([]);
    }
  }, [
    history,
    isDone,
    selectedCompletion,
    selectedInteraction,
    onPerConvoField,
    useChatbotV2,
    rlhfParams,
  ]);

  useEffect(() => {
    if (
      (conversationEndedEarly || selectedInteraction === maxNumberOfInteractions) &&
      selectedInteraction === history.interactions.length &&
      perConversationFields.length > 0
    )
      setOnPerConvoField(true);
    else setOnPerConvoField(false);
  }, [
    selectedInteraction,
    conversationEndedEarly,
    history.interactions.length,
    perConversationFields,
  ]);

  const nextInteraction = () => {
    if (reviewNoBranching) {
      submitChatbotForm();
    } else {
      setInteractionNumber(interactionNumber + 1);
    }
  };

  const previousInteraction = () => {
    if (interactionNumber >= 0) {
      setInteractionNumber(interactionNumber - 1);
    }
  };

  const jumpToEnd = () => {
    setInteractionNumber(history.interactions.length || -1);
  };

  const erroredItems = useMemo(() => {
    if (!lintErrors) return [];
    const errors = _.filter(lintErrors, e => e.severity === LintSeverity.ERROR && !e.dismissed);
    if (!errors || errors.length < 1) return [];
    const _annotations = errors[0].data.taskComponentAnnotations;
    if (_annotations) {
      return _annotations.map(t => t.id.substring(t.id.indexOf('_') + 1));
    }
    return [];
  }, [lintErrors]);

  const hasItemsOnRankingEditPage = useMemo(() => {
    return (
      rlhfParams &&
      (rlhfParams!.numberOfCompletions > 1 ||
        rlhfParams?.editBestCompletion ||
        (isMakingChanges && useEditPrompt))
    );
  }, [rlhfParams, isMakingChanges, useEditPrompt]);

  const submitChatbotFormAsync = async () => {
    if (useChatbotV2) {
      if (selectedCompletion < rlhfParams!.numberOfCompletions && hasItemsOnRankingEditPage) {
        setSelectedCompletion(
          !overrideViewFields
            ? selectedCompletion + 1
            : Math.min(selectedCompletion + 1, rlhfParams!.numberOfCompletions - 1),
        );
        return;
      }
    }

    const noErrors = perTurnCheck ? await perTurnCheck() : true;
    if (noErrors) {
      submitChatbotForm();
    }
  };

  const submitChatbotForm = () => {
    if (useChatbotV2) {
      if (selectedCompletion < rlhfParams!.numberOfCompletions && hasItemsOnRankingEditPage) {
        setSelectedCompletion(
          !overrideViewFields
            ? selectedCompletion + 1
            : Math.min(selectedCompletion + 1, rlhfParams!.numberOfCompletions - 1),
        );
        return;
      }
    }

    // find the top ranked completion
    const index = useChatbotV2
      ? selectedInteraction
      : reviewNoBranching
      ? interactionNumber
      : history.interactions.length - 1;

    const lastInteraction = history.interactions[index];
    const rankingQuestionIndex = index + 1;

    const topRankingResponse =
      getTopCompletionIndex(rankingQuestionIndex) ??
      _.get(annotations as any, 'ranking_multiple_chat_api_endpoints.response[0]');
    const topCompletionIndex: number = (topRankingResponse ?? Number(topRankingResponse)) || 0;
    const chosenCompletion = {
      // use spread operator to make a copy
      ...lastInteraction.completions[topCompletionIndex],
    };
    let editedCompletion = rlhfParams!.editBestCompletion
      ? _.get(annotations as any, `editCompletion_${rankingQuestionIndex}.response[0]`, undefined)
      : undefined;
    const isEdited = rlhfParams!.editBestCompletion
      ? editedCompletion !== chosenCompletion?.text
      : undefined;

    // update the editedCompletion with the code fields if they exist
    const codingFields = Object.values(annotations).filter(f => f.type === 'code');
    if (codingFields.length > 0) {
      const sourceCodeList = codingFields.map((c: any) =>
        c.response && c.response.length > 0 && _.has(c.response[0], 'sourceCode')
          ? c.response[0].sourceCode
          : '',
      );
      const editedCompletionWithCodingFields = addCodeFieldsToMarkdown(
        editedCompletion,
        sourceCodeList,
      );
      editedCompletion = editedCompletionWithCodingFields;
    }

    if (isEdited && rlhfParams!.useEditedResponseInHistory && chosenCompletion) {
      chosenCompletion.text = editedCompletion;
      chosenCompletion.isEdited = true;
    }

    // if we're reviewing with no branching, we should not override the history, just this specific entry.
    if (reviewNoBranching) {
      if (useEditPrompt) {
        const editedPrompt = _.get(
          annotations as any,
          `editPrompt_${rankingQuestionIndex}.response[0]`,
          undefined,
        );
        if (editedPrompt && editedPrompt !== '') {
          lastInteraction.prompt = editedPrompt;
        }
      }
      const _history = _.cloneDeep(history);
      _history.interactions[index] = {
        ...lastInteraction,
        chosenCompletion,
        displayedCompletion:
          editedCompletion && isEdited ? { text: editedCompletion, isEdited } : undefined,
      };
      setHistory(_history);
      setInteractionNumber(interactionNumber + 1);
      return;
    }

    setHistory({
      topic: history.topic,
      interactions: [
        ...history.interactions.slice(0, index),
        {
          ...lastInteraction,
          chosenCompletion,
          displayedCompletion:
            editedCompletion && isEdited ? { text: editedCompletion, isEdited } : undefined,
        },
      ],
    });

    if (useChatbotV2 && chosenCompletion !== undefined) {
      setOverrideViewFields(false);
      setSelectedCompletion(0);
      if (perConversationFields && rankingQuestionIndex === maxNumberOfInteractions) {
        setOnPerConvoField(true);
      }
    }
    if (enableAutoCritique) {
      setCritiqueText('');
      setCritiqueResponseChanged(0);
    }
  };

  const getAutoSpec = (pB: any) => {
    (async () => {
      try {
        const annotationsFormatted: Record<string, any> = _.cloneDeep(annotations);
        for (const key of Object.keys(annotations)) {
          annotationsFormatted[key] = Array.isArray(annotations[key].response)
            ? annotations[key].response.join()
            : annotations[key].response;
          if (Array.isArray(annotationsFormatted[key])) {
            annotationsFormatted[key] = annotationsFormatted[key].join();
          }
          annotationsFormatted[key] = `${annotationsFormatted[key]}`;
        }
        const attachmentsFormatted: any = {};
        for (const [i, attachment] of attachments.entries()) {
          attachmentsFormatted[`attachment_${i + 1}`] = attachment.content ?? '';
        }
        const promptBreakdown = { ...pB, ...annotationsFormatted, ...attachmentsFormatted };
        const result = await API.selfserve.fetchAutoSpec({
          payload: {
            promptBreakdown,
            numCompletions: rlhfParams?.numberOfCompletions ?? 1,
            history,
            projectId,
          },
          taskCustomer,
        });
        if (result.length) {
          setAutoSpecText(result);
        }
      } finally {
        setAutoSpecLoading(false);
      }
    })();
  };

  const handleCohereStreaming = async (prompt: string) => {
    const models = _.get(rlhfParams, 'chatApiAdditionalParams.client_configs', []).map(
      (x: any) => x.model,
    ) as string[];
    if (models.length !== rlhfParams!.numberOfCompletions) {
      throw new Error('Model lengths does not equal to number of completions');
    }

    const PLACEHOLDER_TEXT = '';

    setIsStreaming(true);
    handleSetCandidateBestCompletion('');
    setHistory(prevHistory =>
      produce(prevHistory, draft => {
        draft.interactions.push({
          prompt,
          completions: models.map(model => ({ text: PLACEHOLDER_TEXT, model })),
        });
      }),
    );

    const messages = getMessagesFromCompletedInteractions(history.interactions);

    let preamble = '';
    if (cohereIncludePreambleAndSeed) {
      const seed = metadata?.chat_history ?? [];
      preamble = metadata?.preamble_override;
      const formattedSeed = seed.map((s: any) => {
        return { role: s.user_name === 'User' ? MessageRole.User : 'chatbot', content: s.text };
      });
      messages.unshift(...formattedSeed);
    }

    messages.push({
      role: MessageRole.User,
      content: prompt,
    });
    try {
      for (let index = 0; index < models.length; index++) {
        const model = models[index];
        let responseText = '';
        const body = {
          strategyName: StrategyName.CohereStrategy,
          params: {
            model: _.get(rlhfParams, `chatApiAdditionalParams.client_configs[${index}].model`, ''),
            preamble_override: preamble,
            projectId,
            stream: true,
          },
          messages,
        };
        const ac = new AbortController();
        setAbortController(ac);
        try {
          await apiFetch(null, 'internal/self_serve/rlhf/createCompletion', {
            method: 'POST',
            body,
            fetchEventSourceOptions: {
              signal: ac.signal,
              openWhenHidden: true,
              onmessage: event => {
                if (event.data === '[DONE]' || event.data === '[IGNORE]') {
                  return;
                }
                const parsed = JSON.parse(event.data);
                if (parsed.error) {
                  throw new Error(parsed.error);
                }
                if (parsed.output) {
                  responseText += parsed.output;
                }
                debouncedSetHistory(index, model, responseText);
              },
              onclose: () => {
                debouncedSetHistory(index, model, responseText);
              },
              onerror: e => {
                logger.error(`Cohere streaming error ${e}`, {
                  assignmentId,
                  body: JSON.stringify(body),
                });
                throw e;
              },
            },
          });
        } catch (e) {
          if (e.name !== 'AbortError') {
            throw e;
          }
        }

        if (responseText.length === 0) {
          throw new Error('Empty response from Cohere.');
        }
        if (models.length === 1 && rlhfParams?.editBestCompletion) {
          handleSetCandidateBestCompletion(responseText);
        }
      }
    } catch (e) {
      // avoid potential race condition with debouncedSetHistory
      setTimeout(() => {
        setHistory(prevHistory =>
          produce(prevHistory, draft => {
            draft.interactions = draft.interactions.slice(0, -1);
          }),
        );
      }, 40);
      throw e;
    } finally {
      setIsStreaming(false);
    }
  };

  // to reduce UI lag
  const debouncedSetHistory = React.useCallback(
    _.debounce((index, model, responseText) => {
      setHistory(prevHistory =>
        produce(prevHistory, draft => {
          draft.interactions[draft.interactions.length - 1].completions[index] = {
            model,
            text: responseText,
          };
        }),
      );
    }, 20),
    [setHistory, produce],
  );

  const handleFlamingoStreaming = async (prompt: string, promptBreakdown: any) => {
    // --------------------  Step 0: Get models ----------------------------------
    setIsStreaming(true);

    const models: string[] = [];
    invariant(
      _.isArray(rlhfParams?.chatApiMultiEndpoints) && rlhfParams?.chatApiMultiEndpoints.length,
    );
    for (const endpoint of rlhfParams!.chatApiMultiEndpoints!) {
      if (endpoint !== 'https://api.openai.com/v1/chat/completions') {
        // Example format: https://www.fairllms.com/s-smp-230405/completions
        models.push(endpoint.split('/')[3]);
      }
    }
    if (rlhfParams!.chatApiMultiEndpoints!.includes('https://api.openai.com/v1/chat/completions')) {
      models.push('openai'); // placeholder
    }

    // --------------------  Step 1: Get JWT Tokens ----------------------------------
    const streamingJWTToken = await API.selfserve.fetchJWTForStreaming();
    invariant(_.isString(streamingJWTToken));

    // --------------------  Step 2: Get Flamingo Model Streaming ID ----------------------------------
    const messages = getMessagesFromCompletedInteractions(history.interactions);
    messages.push({
      role: MessageRole.User,
      content: prompt,
    });

    let modelStreamingIds: string[] = [];

    try {
      modelStreamingIds = (
        await Promise.all(
          models
            .filter(model => model !== 'openai')
            .map(flamingoModel => {
              return apiFetch(
                null,
                `https://www.remotasks.com/flamingo-llm/${flamingoModel}/completions_stream`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'x-scale-routing-jwt': `Bearer ${streamingJWTToken}`,
                  },
                  body: JSON.stringify({ messages }),
                },
              );
            }),
        )
      ).map(data => data.id);
    } catch (e) {
      logger.error('Failed to fetch model streaming ids');
    }
    if (rlhfParams!.chatApiMultiEndpoints!.includes('https://api.openai.com/v1/chat/completions')) {
      modelStreamingIds.push('openai'); // placeholder
    }

    const modelResponses: string[] = new Array(models.length).fill('');

    // --------------------  Step 3: Get the streaming response ----------------------------------
    for (let i = 0; i < modelStreamingIds.length; i++) {
      const modelStreamingId = modelStreamingIds[i];
      const model = models[i];
      const isOpenAI = model === 'openai';

      const headers: any = {
        'Content-Type': 'application/json',
      };
      if (!isOpenAI) headers['x-scale-routing-jwt'] = `Bearer ${streamingJWTToken}`;

      const url = isOpenAI
        ? 'internal/self_serve/rlhf/createCompletion'
        : `https://www.remotasks.com/flamingo-llm/${model}/stream_completions/${modelStreamingId}`;

      await API.selfserve.fetchSSE(url, {
        method: 'POST',
        headers,
        body: isOpenAI
          ? JSON.stringify({
              strategyName: StrategyName.OpenAIStrategy,
              params: {
                model: 'gpt-4',
                temperature: 0,
                max_tokens: 1024,
                stream: true,
              },
              messages,
              projectId,
            })
          : undefined,
        fetchEventSourceOptions: {
          openWhenHidden: true,
          onopen: async response => {
            if (!response.ok) {
              throw new Error('Failed to open SSE connection');
            }
            setHistory(prevHistory =>
              produce(prevHistory, draft => {
                const completions = modelResponses.map((response, index) => {
                  return {
                    model: models[index],
                    text: response,
                  };
                });
                const newInteraction: Interaction = {
                  prompt,
                  completions,
                  promptBreakdown,
                };
                if (i === 0) {
                  draft.interactions.push(newInteraction);
                }
              }),
            );
          },
          onmessage: event => {
            if (isOpenAI) {
              if (event.data === '[DONE]') {
                return;
              }
              const parsed = JSON.parse(event.data);
              if (parsed.error) {
                throw new Error(parsed.error);
              }
              if (parsed.choices?.[0]?.delta?.content) {
                modelResponses[i] += parsed.choices[0].delta.content;
              }
            } else {
              modelResponses[i] = event.data;
            }

            setHistory(prevHistory =>
              produce(prevHistory, draft => {
                const completions = modelResponses.map((response, index) => {
                  return {
                    model: models[index],
                    text: response,
                  };
                });
                const newInteraction: Interaction = {
                  prompt,
                  completions,
                  promptBreakdown,
                };
                if (isRangedMultiTurn) {
                  draft.interactions[draft.interactions.length - 1] = newInteraction;
                } else {
                  draft.interactions = [newInteraction];
                }
              }),
            );
          },
          onerror: e => {
            logger.error(`Flamingo streaming error ${e}`, {
              assignmentId,
              prompt,
            });
            throw new Error(e);
          },
          onclose: () => {
            logEvent(Event.RLHFResponseTime, {
              modelProvider: isOpenAI ? 'openai' : 'flamingo',
              modelName: model,
              outputText: modelResponses[i],
              outputTextCount: -1,
              promptText: prompt,
              streamingId: modelStreamingId,
              event: 'final_message',
            });
          },
        },
      });

      if (modelResponses[i].length === 0) {
        throw new Error(`Empty response from endpoint ${modelStreamingId}`);
      }
    }
    setIsStreaming(false);
  };

  const handleSimulateBenchmarkResponse = async (
    prompt: string,
    promptBreakdown: any,
    response: GetCompletionsResponse,
  ) => {
    setIsStreaming(true);

    const completions = response.completions;

    setHistory(prevHistory =>
      produce(prevHistory, draft => {
        draft.interactions.push({
          prompt,
          completions: completions.map(completion => ({
            model: completion.model,
            text: '',
          })),
          promptBreakdown,
        });
      }),
    );

    const TOKEN_SIZE = 5;
    for (let completionIndex = 0; completionIndex < completions.length; completionIndex++) {
      const completionText = completions[completionIndex].text;
      let characterCount = 1;
      while (characterCount <= completionText.length) {
        setHistory(prevHistory =>
          produce(prevHistory, draft => {
            draft.interactions[0].completions[completionIndex].text = completionText.slice(
              0,
              completionText.length - characterCount < 2 * TOKEN_SIZE
                ? completionText.length
                : characterCount,
            );
          }),
        );
        characterCount += TOKEN_SIZE;
        await sleep(2);
      }
    }

    setIsStreaming(false);
  };

  const [saveStreamingInteraction, setSaveStreamingInteraction] = useState<number>(0);
  useEffect(() => {
    if (streamingInteraction) {
      setHistory(prevHistory =>
        produce(prevHistory, draft => {
          draft.interactions[draft.interactions.length - 1] = _.cloneDeep(streamingInteraction);
        }),
      );
    }
  }, [saveStreamingInteraction]);

  const handleGenericStreaming = async (prompt: string) => {
    invariant(rlhfParams?.chatApiEndpoints && rlhfParams?.chatApiEndpoints.length > 0);
    const endpoints = rlhfParams?.chatApiEndpoints;

    const messages = getMessagesFromCompletedInteractions(history.interactions).map(message => ({
      role: message.role,
      content: message.content,
    }));
    // ---------- [Start] Crab specific logics ----------
    let preamble = '';
    if (cohereIncludePreambleAndSeed) {
      const seed = metadata?.chat_history ?? [];
      preamble = metadata?.preamble_override;
      const formattedSeed = seed.map((s: any) => {
        return { role: s.user_name === 'User' ? MessageRole.User : 'chatbot', content: s.text };
      });
      messages.unshift(...formattedSeed);
    }
    // ---------- [End] Crab specific logics ----------
    messages.push({
      role: MessageRole.User,
      content: prompt,
    });

    const createCompletionRequestParams: any[] = [];
    for (const endpoint of endpoints) {
      const { url, params } = endpoint;
      const strategyName = getStrategyFromUrl(url);
      const body = {
        strategyName,
        params,
        messages,
        projectId,
        assignmentId,
      };
      createCompletionRequestParams.push(body);
    }

    // ---------- [Start] Crab specific logics ----------
    for (const param of createCompletionRequestParams) {
      if (param.strategyName === StrategyName.CohereStrategy) {
        _.set(param, 'preamble_override', preamble);
      }
    }
    // ---------- [End] Crab specific logics ----------

    setIsStreaming(true);

    setHistory(prevHistory =>
      produce(prevHistory, draft => {
        draft.interactions.push({
          prompt,
          completions: endpoints.map(endpoint => ({
            model: _.get(endpoint, 'params.model', endpoint.url),
            text: '',
          })),
        });
      }),
    );
    setStreamingInteraction({
      prompt,
      completions: endpoints.map(endpoint => ({
        model: _.get(endpoint, 'params.model', endpoint.url),
        text: '',
      })),
    });

    try {
      for (let i = 0; i < endpoints.length; i++) {
        let responseText = '';

        const ac = new AbortController();
        setAbortController(ac);
        try {
          await apiFetch(null, 'internal/self_serve/rlhf/createCompletion', {
            signal: ac.signal,
            method: 'POST',
            body: createCompletionRequestParams[i],
            fetchEventSourceOptions: {
              signal: ac.signal,
              openWhenHidden: true,
              onmessage: event => {
                if (event.data === '[DONE]' || event.data === '[IGNORE]') {
                  return;
                }
                const parsed = JSON.parse(event.data);
                if (parsed.error) {
                  throw new Error(parsed.error);
                } else if (parsed.output) {
                  responseText += parsed.output;
                }
                // OpenAI's endpoints return a different format
                else if (parsed.choices?.[0]?.delta?.content) {
                  responseText += parsed.choices[0].delta.content as string;
                }
                // Log an error if none of these conditions are met
                else {
                  logger.error(`Unexpected parsing error during streaming`, {
                    assignmentId,
                    parsed,
                  });
                }
                setStreamingInteraction(prevStreamingInteraction =>
                  produce(prevStreamingInteraction, draft => {
                    if (!draft) return;
                    draft.completions[i].text = responseText;
                  }),
                );
              },
              onerror: e => {
                logger.error('Failed to get streamed response from RLHF endpoint.', {
                  e: e.toString(),
                  endpoint: endpoints[i].url,
                  body: JSON.stringify(createCompletionRequestParams[i]),
                  assignmentId,
                });
                throw e;
              },
            },
          });
          setSaveStreamingInteraction(prev => prev + 1);
        } catch (e) {
          if (e.name === 'AbortError') {
            // for the Stop use case, show the partially generated completion
            setSaveStreamingInteraction(prev => prev + 1);
          } else {
            throw e;
          }
        }
        if (responseText.length === 0) {
          throw new Error(`ModelEndpointError: Empty response from endpoint ${endpoints[i].url}`);
        }
      }
    } catch (e) {
      setTimeout(() => {
        setHistory(prevHistory =>
          produce(prevHistory, draft => {
            draft.interactions = draft.interactions.slice(0, -1);
          }),
        );
      }, 40);
      throw e;
    } finally {
      setStreamingInteraction(undefined);
      setIsStreaming(false);
    }
  };

  const handleSubmitPromptStreaming = async (prompt: string, promptBreakdown: any) => {
    if (rlhfParams?.chatApiEndpoints && rlhfParams?.chatApiEndpoints.length > 0) {
      await handleGenericStreaming(prompt);
    } else if (cohereEnableStreaming) {
      // Crab legacy
      await handleCohereStreaming(prompt);
    } else if (useFairStreaming) {
      // Flamingo
      await handleFlamingoStreaming(prompt, promptBreakdown);
    }
    setSelectedInteraction(selectedInteraction + 1);
    // if there are no per interaction fields, we force it to the 'ranking' option in chatbot v2
    if (perResponseFields?.length === 0) {
      setSelectedCompletion(rlhfParams!.numberOfCompletions - 1);
    }
  };

  const submitPrompts = async (
    prompts: string[],
    topic?: string,
    metadata?: Record<string, any>,
  ) => {
    if (
      isDone &&
      // Tasker can re-generate output if search term was updated
      metadata?.operation !== BulbaBardEndpointOperation.BARD_SEARCH_OVERRIDE
    )
      return;

    const _history: ChatHistory = {
      ...history,
      topic,
    };
    const combinedPrompt = prompts.join('\n');

    const promptBreakdown: any = {};
    prompts.forEach((prompt, i) => {
      promptBreakdown[chatBoxTexts[i]] = prompt;
    });

    if (rlhfParams?.autoSpec) {
      setAutoSpecLoading(true);
      getAutoSpec(promptBreakdown);
    }

    if (enableStreaming && !isBenchmark) {
      try {
        await handleSubmitPromptStreaming(combinedPrompt, promptBreakdown);
      } catch (e) {
        if (e.message.startsWith('ModelEndpointError') && isStudio) {
          SAlert.error(e.message, {
            timeout: 10000,
          });
        } else {
          SAlert.error('Something went wrong. Please try again.', {
            timeout: 10000,
          });
        }
        throw e;
      }
      return;
    }

    let response: GetCompletionsResponse;
    let gptBaselineResponse = '';
    if (cohereSkipLongPrompt && combinedPrompt.split(' ').length >= 2000) {
      response = {
        completions: [
          {
            model: 'writeyourown',
            text: '(Input too long for model response. Please provide your own response.)',
          },
        ],
      };
    } else {
      const additionalParams = {
        ...(rlhfParams!.chatApiAdditionalParams || {}),
        ...(metadata || {}),
      };

      const fetchSelfServeChatResponse = () => {
        return API.selfserve.fetchResponsesFromChatEndpoint({
          endpointUrl: rlhfParams!.chatApiEndpoint,
          getCompletionsRequest: {
            numCompletions: rlhfParams!.numberOfCompletions,
            nextPrompt: combinedPrompt,
            history: _history,
            additionalParams,
            chatApiMultiEndpoints: rlhfParams?.chatApiMultiEndpoints,
          },
          taskCustomer,
          assignmentId,
        });
      };

      const fetchGPT4BaselineResponse = () => {
        return apiFetch(null, `internal/self_serve/rlhf/bulbaBaselineResponse`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskId: assignmentId, userPrompt: combinedPrompt }),
        });
      };

      // Generate GPT4 baseline for Bulba Open Rewrite projects
      if (
        rlhfParams!.numberOfCompletions === 1 &&
        rlhfParams!.numberOfInteractions === 1 &&
        rlhfParams!.chatApiEndpoint === 'https://34.105.44.178/api_v1' &&
        enableModelResponseComparisonModal
      ) {
        const [bardResponse, gptResponse] = await Promise.all([
          fetchSelfServeChatResponse(),
          fetchGPT4BaselineResponse(),
        ]);
        response = bardResponse;
        gptBaselineResponse = gptResponse.response;
      } else {
        response = await fetchSelfServeChatResponse();
      }
    }

    if (isBenchmark) {
      await handleSimulateBenchmarkResponse(prompts.join('\n'), promptBreakdown, response);
      return;
    }

    response.completions = response.completions.map(s => ({ ...s, text: s.text.trim() }));

    const newInteraction: Interaction = {
      prompt: combinedPrompt,
      completions: response.completions,
      promptBreakdown,
    };

    if (gptBaselineResponse) {
      newInteraction.baselineResponse = gptBaselineResponse;
    }

    // hack for bulba to store time of request, and operation performed
    if (response?.additionalData?.timeOfRequest) {
      newInteraction.timeOfRequest = new Date(response.additionalData.timeOfRequest);
    }
    if (response?.additionalData?.operation) {
      newInteraction.metadata = {
        operation: response.additionalData.operation,
      };
    }

    // automatically choose next completion if only 1 option
    // and there are no per completion questions
    if (
      !useChatbotV2 &&
      response.completions.length === 1 &&
      perResponseFields.length === 0 &&
      !rlhfParams?.editBestCompletion
    ) {
      newInteraction.chosenCompletion = response.completions[0];
    }
    setCurrentOptionIndicesForRanking(_.range(response.completions.length));
    handleSetCandidateBestCompletion(undefined);

    if (useChatbotV2) {
      setSelectedInteraction(selectedInteraction + 1);
      // if there are no per interaction fields, we force it to the 'ranking' option in chatbot v2
      if (perResponseFields?.length === 0) {
        setSelectedCompletion(rlhfParams!.numberOfCompletions - 1);
      }
    }

    if (rlhfParams?.isFair) {
      setCurrentOptionIndicesForRanking(_.range(response.completions.length));
      setHistory({
        ..._history,
        interactions: [..._history.interactions, newInteraction],
      });
      setAdditionalResponseData(response.additionalData);
    } else {
      setHistory({
        ..._history,
        interactions: [..._history.interactions, newInteraction],
      });
      setAdditionalResponseData(response.additionalData);
    }
  };

  const resetChat = () => {
    if (!(rlhfParams?.isFair || isRangedMultiTurn || canResetChat)) return;
    setCurrentOptionIndicesForRanking([]);
    setHistory({ interactions: [] });
    setSelectedCompletion(0);
    if (useChatbotV2) setSelectedInteraction(-1);
  };

  const gotoSelectedInteraction = (i: number) => {
    if (isStreaming) return;

    if (
      (i === maxNumberOfInteractions ||
        (i === history.interactions.length && conversationEndedEarly)) &&
      perConversationFields
    ) {
      setOnPerConvoField(true);
      setOverrideViewFields(false);
      setSelectedInteraction(i);
    } else {
      setOnPerConvoField(false);
      setSelectedInteraction(i);
      setOverrideViewFields(true);
    }
    setSelectedCompletion(0);
  };

  const hideReviewNav =
    (!readOnly && !reviewNoBranching) ||
    (history.interactions.length === 1 && perConversationFields.length === 0);

  useEffect(() => {
    setCritiqueResponseChanged(p => p + 1);
  }, [finalResponse]);

  const getCritique = () => {
    (async () => {
      setCritiquing(true);
      try {
        const annotationsFormatted: Record<string, any> = _.cloneDeep(annotations);
        for (const key of Object.keys(annotations)) {
          annotationsFormatted[key] = Array.isArray(annotations[key].response)
            ? annotations[key].response.join()
            : annotations[key].response;
          if (Array.isArray(annotationsFormatted[key])) {
            annotationsFormatted[key] = annotationsFormatted[key].join();
          }
          annotationsFormatted[key] = `${annotationsFormatted[key]}`;
        }
        const attachmentsFormatted: any = {};
        for (const [i, attachment] of attachments.entries()) {
          attachmentsFormatted[`attachment_${i + 1}`] = attachment.content ?? '';
        }
        const interactionIndex = autoCritiqueIndex > 0 ? autoCritiqueIndex - 1 : 0;
        const promptBreakdown = {
          ...history.interactions[interactionIndex]?.promptBreakdown,
          ...annotationsFormatted,
          ...attachmentsFormatted,
        };
        const result = await API.selfserve.fetchAutoCritique({
          payload: {
            promptBreakdown,
            answer: finalResponse,
            projectId,
            history: history.interactions.slice(0, interactionIndex),
          },
          taskCustomer,
        });
        if (result.length) {
          const text = result
            .split(/\r?\n|\r|\n/g)
            .filter((t: string) => !t.startsWith('Example response:'))
            .join('\n');
          setCritiqueText(text);
          setCritiqueBlockSubmit(true);
          setTimeout(() => setCritiqueBlockSubmit(false), 5000); // to ensure the crtique is read
        }
      } finally {
        setCritiqueResponseChanged(0);
        setCritiquing(false);
      }
    })();
  };

  return (
    <ChatbotRankingContext.Provider
      value={{
        enabled,
        readOnly,
        isReview,
        history,
        setEnabled,
        setReadOnly: setReadOnlyWrapper,
        setIsReview,
        nextInteraction:
          interactionNumber < history.interactions.length ? nextInteraction : undefined,
        previousInteraction: interactionNumber >= 0 ? previousInteraction : undefined,
        jumpToEnd: interactionNumber < history.interactions.length ? jumpToEnd : undefined,
        transcript,
        submitPrompts,
        setTaxonomy,
        fieldsToShow,
        rlhfParams,
        setRLHFParams,
        submitChatbotForm,
        submitChatbotFormAsync,
        rangedMultiTurnEndConversation,
        formatSections,
        isDone,
        rangedMultiTurnCanSubmit,
        annotations,
        setAnnotations,
        taskCustomer,
        setTaskCustomer,
        candidateBestCompletion,
        selectedCompletion,
        setSelectedCompletion,
        useChatbotV2: !!useChatbotV2,
        selectedInteraction,
        setSelectedInteraction,
        onPerConvoField,
        gotoSelectedInteraction,
        overrideViewFields,
        perConversationFields,
        perResponseFields,
        perTurnFields,
        erroredItems,
        checkIfItemBlank,
        isMakingChanges,
        hideReviewNav,
        hideNextButton,
        nextButtonDisabled,
        shouldUpdateBestCompletion,
        setShouldUpdateBestCompletion,
        chatBoxTexts,
        setChatBoxTexts,
        canResetChat,
        resetChat,
        isStreaming,
        autoSpecText,
        setAutoSpecText,
        autoSpecLoading,
        setAutoSpecLoading,
        critiqueText,
        setCritiqueText,
        critiqueResponseChanged,
        setAttachments,
        isRangedMultiTurn,
        handleUndo,
        stopStreaming: isStudio ? stopStreaming : undefined,
        startBranchConversation,
        reviewNoBranching,
        candidateEditedPrompt,
        interactionNumber,
        hasItemsOnRankingEditPage,
        additionalResponseData,
        setAdditionalResponseData,
        useEditPrompt,
        skipPromptValidation,
        streamingInteraction,
        resetLastInteraction,
      }}
    >
      {children}
    </ChatbotRankingContext.Provider>
  );
};

export const useChatbotContext = () => useContext(ChatbotRankingContext);
